import { useEffect, useRef, useState } from "react";

// File: BankSelect.tsx
interface Bank {
    id: number;  // Thêm id
    code: string;
    shortName: string;
    name: string;
    logoUrl: string;
}

interface BankSelectProps {
    label: string;
    value: string | number;  // Có thể là string hoặc number
    banks: Bank[];
    onChange: (value: number) => void;  // Trả về id (number)
    disabled?: boolean;
}

function BankSelect({ label, value, banks, onChange, disabled = false }: BankSelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const containerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Tìm bank được chọn theo id
    const selectedBank = banks.find(bank => bank.id === Number(value));

    // Lọc và sort banks theo alphabet
    const filteredBanks = banks
        .filter(bank =>
            bank.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            bank.shortName.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .sort((a, b) => {
            const compareShortName = a.shortName.localeCompare(b.shortName, 'vi');
            if (compareShortName !== 0) return compareShortName;
            return a.name.localeCompare(b.name, 'vi');
        });

    // Đóng dropdown khi click bên ngoài
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchTerm("");
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelect = (bank: Bank) => {
        onChange(bank.id);  // Trả về id thay vì shortName
        setIsOpen(false);
        setSearchTerm("");
    };

    return (
        <div className="relative w-full" ref={containerRef}>
            <div className="flex flex-col w-full">
                <label className="font-medium mb-[4px]">{label}</label>

                {/* Selected Value Display */}
                <div
                    className={`
                        outline-none border border-[#e0e0e0] rounded-[4px] py-[4px] px-[8px] 
                        w-full h-[46px] flex items-center justify-between cursor-pointer
                        transition-all duration-200
                        ${disabled ? 'opacity-45 cursor-not-allowed' : 'hover:border-[#3498db]'}
                        ${isOpen ? 'border-[#3498db] ring-1 ring-[#3498db]' : ''}
                    `}
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                >
                    {selectedBank ? (
                        <div className="flex items-center gap-2 overflow-hidden">
                            <img
                                src={selectedBank.logoUrl}
                                alt={selectedBank.shortName}
                                className="w-6 h-6 object-contain flex-shrink-0"
                                onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                }}
                            />
                            <span className="text-sm truncate">{selectedBank.shortName}</span>
                        </div>
                    ) : (
                        <span className="text-gray-400 text-sm">Chọn ngân hàng</span>
                    )}
                    <i className={`fa fa-chevron-down text-xs transition-transform duration-200 flex-shrink-0 ml-2 ${isOpen ? 'rotate-180' : ''}`} />
                </div>
            </div>

            {/* Dropdown */}
            {isOpen && !disabled && (
                <div
                    ref={dropdownRef}
                    className="absolute left-0 top-[calc(100%+4px)] w-full bg-white border border-[#e0e0e0] rounded-[4px] shadow-lg max-h-[320px] overflow-hidden z-[9999]"
                    style={{ minWidth: '100%' }}
                >
                    {/* Search Input */}
                    <div className="p-2 border-b bg-white sticky top-0 z-10">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Tìm kiếm ngân hàng..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-[4px] outline-none focus:border-[#3498db] focus:ring-1 focus:ring-[#3498db] text-sm"
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm("")}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <i className="fa fa-times" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Bank List */}
                    <div className="overflow-y-auto max-h-[260px]">
                        {filteredBanks.length > 0 ? (
                            filteredBanks.map((bank) => (
                                <div
                                    key={bank.id}
                                    className={`
                                        flex items-center gap-3 px-3 py-2.5 cursor-pointer
                                        hover:bg-blue-50 transition-colors
                                        ${bank.id === Number(value) ? 'bg-blue-100' : ''}
                                    `}
                                    onClick={() => handleSelect(bank)}
                                >
                                    <img
                                        src={bank.logoUrl}
                                        alt={bank.shortName}
                                        className="w-8 h-8 object-contain flex-shrink-0"
                                        onError={(e) => {
                                            e.currentTarget.style.display = 'none';
                                        }}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-sm text-gray-900">{bank.shortName}</div>
                                        <div className="text-xs text-gray-500 truncate">{bank.name}</div>
                                    </div>
                                    {bank.id === Number(value) && (
                                        <i className="fa fa-check text-blue-600 flex-shrink-0" />
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="px-3 py-6 text-center text-gray-500 text-sm">
                                <i className="fa fa-search mb-2 text-gray-400" />
                                <div>Không tìm thấy ngân hàng</div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default BankSelect;